import { useEffect, useState, type FormEvent } from "react";
import { Clock3, Pause, Play, Wrench } from "lucide-react";
import { startGoogleSession } from "../lib/auth";
import { updateMaintenanceControl } from "../lib/api";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatCountdown, formatTimestamp } from "../lib/timeZone";
import { GuardedChangeForm } from "./GuardedChangeForm";
import type { MaintenanceControl, Viewer } from "../types";
import { DEPLOYMENT_NAME } from "../lib/runtime";

const EXPIRY_CHOICES = [15, 30, 60, 120, 240];

function expiryLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

export function MaintenancePanel({ viewer, control, loading, onChange }: {
  viewer: Viewer;
  control: MaintenanceControl | null;
  loading: boolean;
  onChange: (next: MaintenanceControl) => void;
}) {
  const { timeZone } = useTimeZone();
  const [armed, setArmed] = useState(false);
  const [minutes, setMinutes] = useState(30);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const paused = control?.enabled === true;
  const phrase = paused ? "DISABLE MAINTENANCE" : "ENABLE MAINTENANCE";
  const canChange = viewer.role === "superAdmin";

  // Keep the "turns back on" countdown honest while an incident window runs.
  useEffect(() => {
    if (!paused) return;
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const cancel = () => { setArmed(false); setReason(""); setConfirmation(""); setError(null); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true); setError(null);
    try {
      await startGoogleSession();
      onChange(await updateMaintenanceControl({
        enabled: !paused,
        expiresInMinutes: paused ? null : minutes,
        reason: reason.trim(),
        confirmation
      }));
      setArmed(false); setReason(""); setConfirmation("");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "The app could not be paused or reopened");
    } finally {
      setSaving(false);
    }
  };

  const countdown = paused ? formatCountdown(control?.expiresAt ?? null, now) : null;
  const recorded = Boolean(control?.changedAt || control?.changedBy || control?.reason);

  return (
    <section className={paused ? "control-panel is-critical" : "control-panel"} aria-labelledby="maintenance-heading">
      <header className="control-panel-heading">
        <span className="control-panel-icon"><Wrench size={20} aria-hidden="true" /></span>
        <div>
          <h2 id="maintenance-heading">Pause the app</h2>
          <p>Replace the app with a maintenance message while you work on it.</p>
        </div>
        <span className={paused ? "control-state-pill is-critical" : "control-state-pill is-healthy"}>
          <span />{loading ? "Loading" : paused ? "Paused" : "Open"}
        </span>
      </header>
      <div className="control-panel-body">
        <p className="control-panel-state">
          {loading && !control
            ? "Reading the current setting…"
            : paused
              ? "Everyone who opens the app right now sees the maintenance message and cannot get past it."
              : `The app is not paused. Anyone with a ${DEPLOYMENT_NAME} build can use it normally.`}
        </p>
        {paused || recorded ? (
          <dl className="control-facts">
            {paused ? (
              <div>
                <dt>Turns back on by itself</dt>
                <dd>{formatTimestamp(control?.expiresAt ?? null, timeZone)}{countdown ? ` · ${countdown}` : ""}</dd>
              </div>
            ) : null}
            <div><dt>{paused ? "Paused" : "Last changed"}</dt><dd>{formatTimestamp(control?.changedAt ?? null, timeZone)}</dd></div>
            <div><dt>By</dt><dd>{control?.changedBy ?? "—"}</dd></div>
            <div><dt>Reason given</dt><dd>{control?.reason ?? "—"}</dd></div>
          </dl>
        ) : null}
        {armed ? (
          <GuardedChangeForm
            title={paused ? "Reopen the app" : "Pause the app for everyone"}
            tone={paused ? undefined : "danger"}
            impact={paused
              ? "The maintenance message disappears straight away and people can use the app again."
              : `Everyone using the app is blocked immediately. It reopens by itself after ${expiryLabel(minutes)}, even if nobody comes back to this page.`}
            phrase={phrase}
            reason={reason}
            onReasonChange={setReason}
            reasonPlaceholder={paused ? "What was fixed?" : "What are you working on?"}
            confirmation={confirmation}
            onConfirmationChange={setConfirmation}
            requirements={[{ met: canChange, label: "super-admin access" }]}
            submitLabel={paused ? "Reopen the app" : "Pause the app"}
            saving={saving}
            error={error}
            onCancel={cancel}
            onSubmit={(event) => void submit(event)}
          >
            {paused ? null : (
              <label className="stacked-field">
                Reopen the app automatically after
                <select value={minutes} onChange={(event) => setMinutes(Number(event.target.value))}>
                  {EXPIRY_CHOICES.map((choice) => <option key={choice} value={choice}>{expiryLabel(choice)}</option>)}
                </select>
                <small><Clock3 size={13} aria-hidden="true" />You can reopen it sooner from this page at any time.</small>
              </label>
            )}
          </GuardedChangeForm>
        ) : (
          <div className="control-panel-actions">
            <button
              type="button"
              className={paused ? "primary-button" : "danger-outline-button"}
              disabled={!canChange || loading || !control}
              onClick={() => { setArmed(true); setError(null); }}
            >
              {paused ? <><Play size={16} aria-hidden="true" />Reopen the app</> : <><Pause size={16} aria-hidden="true" />Pause the app</>}
            </button>
            {canChange ? null : <p className="control-panel-note">Only a super-admin can pause or reopen the app.</p>}
          </div>
        )}
      </div>
    </section>
  );
}
